import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { PageHeader } from '@/components/ui/page-header';
import { SEOHead } from '@/components/SEO/SEOHead';
import { StructuredData } from '@/components/SEO/StructuredData';
import { useMembersDirectoryOptimized } from '@/hooks/useMembersDirectoryOptimized';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { useOptimizedPresence } from '@/hooks/useOptimizedPresence';
import { usePermissions } from '@/hooks/usePermissions';
import { Member } from '@/types/member';
import ConnectionRequestDialog from '@/components/members/ConnectionRequestDialog';
import { SubscriptionRestriction } from '@/components/ui/subscription-restriction';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getLastActiveText, getRealisticUserStatus } from '@/utils/presenceUtils';
import { useOptimizedChat } from '@/hooks/useOptimizedChat';
// FuzzySearchBar removed - now using HybridIntelligentSearchBar

// Advanced Directory Components
import { AdvancedDirectoryHeader } from '@/components/directory/AdvancedDirectoryHeader';
import { AdvancedMemberFilters } from '@/components/directory/AdvancedMemberFilters';
import { SmartTaggedMemberCardMemo } from '@/components/members/SmartTaggedMemberCardMemo';
import { DirectoryMemberCard } from '@/components/members/DirectoryMemberCard';
import { MemberMapView } from '@/components/directory/MemberMapView';
import { DirectoryAnalytics } from '@/components/directory/DirectoryAnalytics';
import { MemberProfileModal } from '@/components/directory/MemberProfileModal';
import { useContactRequests } from '@/hooks/useContactRequests';
import { supabase } from '@/integrations/supabase/client';

const Members = () => {
  const { user, isAdmin, subscription, member, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { getOrCreateConversation } = useOptimizedChat();
  
  // Session check removed - handled by ProtectedRoute
  
  // 🔥 ÉTAPE 4: Détection de timeout de chargement
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // 🔥 PHASE 2: Charger shared_contacts une seule fois au niveau parent
  const { sharedContacts } = useContactRequests();
  
  // 🎯 CORRECTION: Tenir compte des abonnements admin ET subscription
  const canAccessMembers = isAdmin || (member?.subscription_type && member.subscription_type !== 'free');

  const {
    members,
    allMembers,
    loading,
    loadingMore,
    error,
    hasMore,
    filters,
    filterValues,
    memberStats,
    onlineMembers,
    updateFilters,
    resetFilters,
    setViewMode,
    toggleFilters,
    updateSearchTerm,
    loadMoreMembers,
  } = useMembersDirectoryOptimized(user?.id);

  // Real-time connection status tracking
  const { 
    getConnectionStatus: getStatus, 
    sendRequest, 
    respondToRequest,
    getIncomingRequests 
  } = useConnectionStatus(user?.id);

  // Real-time presence tracking
  const { getPresence, myPresence } = useOptimizedPresence();
  
  // Permissions check for viewing last active time
  const { hasPermission } = usePermissions();
  const canViewActivity = hasPermission('view_member_activity');
  
  // Debug log for presence status
  console.log('🟢 Members page presence check:', {
    totalMembers: members.length,
    onlineCount: memberStats.online,
    myStatus: myPresence?.status,
    myIsActive: myPresence?.is_active
  });

  // Memoize connection status getter
  const getConnectionStatus = useCallback((member: Member) => {
    if (!user?.id || !member.user_id) return null;
    return getStatus(member.user_id);
  }, [getStatus, user?.id]);

  // Dialog states
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Handlers
  const handleRequestConnection = useCallback((member: Member) => {
    setSelectedMember(member);
    setIsConnectionDialogOpen(true);
  }, []);

  const handleViewProfile = useCallback((member: Member) => {
    setSelectedMember(member);
    setIsProfileModalOpen(true);
  }, []);

  const handleMessage = useCallback(async (member: Member) => {
    if (!member.user_id) {
      toast.error('Impossible de démarrer la conversation');
      return;
    }

    try {
      const conversation = await getOrCreateConversation(member.user_id);
      
      if (conversation) {
        navigate(`/chat?conversation=${conversation.id}`);
        toast.success(`Conversation avec ${member.name} ouverte`);
      } else {
        toast.error('Impossible de créer la conversation');
      }
    } catch (error) {
      console.error('Error opening conversation:', error);
      toast.error('Erreur lors de l\'ouverture de la conversation');
    }
  }, [getOrCreateConversation, navigate]);

  const handleFavorite = useCallback((member: Member) => {
    toast.success(`${member.name} ajouté aux favoris`);
    // TODO: Implement favorites
  }, []);

  const handleShare = useCallback(async (member: Member) => {
    const shareUrl = `${window.location.origin}/members/${member.id}`;
    
    try {
      // Try native sharing API first
      if (navigator.share) {
        await navigator.share({
          title: `Profil de ${member.name}`,
          text: `Découvrez le profil de ${member.name}`,
          url: shareUrl
        });
        toast.success('Profil partagé avec succès');
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Lien copié dans le presse-papiers');
      }
    } catch (error) {
      // Final fallback
      toast.error('Impossible de partager le profil');
      console.error('Share failed:', error);
    }
  }, []);

  const handleClusterSelect = useCallback((members: Member[]) => {
    // Focus on the map region members
    toast.info(`${members.length} membres dans cette région`);
  }, []);

  const handleAcceptConnection = useCallback(async (member: Member) => {
    try {
      // Get incoming requests and find the one from this member
      const incomingRequests = getIncomingRequests();
      const request = incomingRequests.find(req => req.requester_id === member.user_id);
      
      if (request) {
        const success = await respondToRequest(request.id, true);
        if (success) {
          toast.success(`Connexion acceptée avec ${member.name}`);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation de la connexion');
    }
  }, [respondToRequest, getIncomingRequests]);

  const handleDeclineConnection = useCallback(async (member: Member) => {
    try {
      // Get incoming requests and find the one from this member
      const incomingRequests = getIncomingRequests();
      const request = incomingRequests.find(req => req.requester_id === member.user_id);
      
      if (request) {
        const success = await respondToRequest(request.id, false);
        if (success) {
          toast.success(`Demande refusée`);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du refus de la connexion');
    }
  }, [respondToRequest, getIncomingRequests]);

  // 🔥 ÉTAPE 4: Détection du timeout de chargement
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
        logger.error('⏱️ Loading timeout detected after 5 seconds');
      }, 5000);
      
      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Annuaire des Membres - First Tuesday"
        description="Découvrez les entrepreneurs, dirigeants, CTO et investisseurs algériens du réseau First Tuesday. Connectez-vous avec l'élite professionnelle algérienne."
        keywords={['annuaire professionnels algériens', 'entrepreneurs algérie', 'networking algérie', 'diaspora algérienne', 'investisseurs algérie']}
        image="/og-images/og-members.png"
        type="website"
        locale="fr"
      />
      
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          itemListElement: [
            { position: 1, name: 'Accueil', item: '/' },
            { position: 2, name: 'Annuaire', item: '/members' }
          ]
        }}
      />
      
      <NavBar />
      
      <PageHeader 
        title="Annuaire des Membres"
        description="Découvrez, connectez et collaborez avec les membres de Singularité"
      />
      
      <main className="container mx-auto px-4 py-8 pb-24">
        {loading ? (
          <div className="space-y-8">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Chargement de l'annuaire...</p>
                
                {/* 🔥 ÉTAPE 4: Fallback UI après 5 secondes */}
                {loadingTimeout && (
                  <div className="mt-6 p-4 bg-destructive/10 border border-destructive/30 rounded-lg max-w-md mx-auto">
                    <p className="text-destructive font-medium mb-3">
                      Le chargement prend plus de temps que prévu
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Réessayer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="text-destructive text-lg">{error}</div>
          </div>
        ) : authLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Vérification des permissions...</p>
            </div>
          </div>
        ) : canAccessMembers ? (
          <div className="space-y-8">
            {/* Recherche hybride maintenant intégrée dans AdvancedDirectoryHeader */}

            {/* Advanced Directory Header */}
            <AdvancedDirectoryHeader
              totalMembers={memberStats.total}
              activeMembers={memberStats.active}
              onlineMembers={memberStats.online}
              viewMode={filters.viewMode}
              onViewModeChange={setViewMode}
              searchTerm={filters.searchTerm}
              onSearchChange={updateSearchTerm}
              showFilters={filters.showFilters}
              onToggleFilters={toggleFilters}
              savedSearches={[]}
              onSaveSearch={() => {}}
              filters={filters}
              onFiltersUpdate={updateFilters}
            />

            {/* Advanced Filters */}
            <AdvancedMemberFilters
              isOpen={filters.showFilters}
              filters={filters}
              onFiltersChange={updateFilters}
              onSaveFilters={() => {}}
              onLoadFilters={() => {}}
              onResetFilters={resetFilters}
              filterValues={filterValues}
              savedFilters={[]}
            />

            {/* Content based on view mode */}
            {filters.viewMode === 'grid' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {members.map((member) => {
                    const presence = getPresence(member.user_id);
                    const userStatus = getRealisticUserStatus(presence);
                    
                    return (
                      <DirectoryMemberCard
                        key={member.id}
                        member={member}
                        currentUserId={user?.id}
                        onViewProfile={handleViewProfile}
                        onMessage={handleMessage}
                        onConnect={handleRequestConnection}
                        userStatus={userStatus}
                        allMembers={members}
                        connectionStatus={getConnectionStatus(member)}
                      />
                    );
                  })}
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center py-6">
                    <button
                      onClick={loadMoreMembers}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span>Chargement...</span>
                        </div>
                      ) : (
                        'Charger plus de membres'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {filters.viewMode === 'list' && (
              <div className="space-y-4">
                {members.map((member, index) => {
                  const presence = getPresence(member.user_id);
                  const userStatus = getRealisticUserStatus(presence);
                  return (
                    <SmartTaggedMemberCardMemo
                      key={member.id}
                      member={member}
                      viewMode="list"
                      userStatus={userStatus}
                      lastActive={canViewActivity ? getLastActiveText(presence) : undefined}
                      currentUserId={user?.id}
                      onViewProfile={handleViewProfile}
                      onMessage={handleMessage}
                      onConnect={handleRequestConnection}
                      onAccept={handleAcceptConnection}
                      onDecline={handleDeclineConnection}
                      onFavorite={handleFavorite}
                      onShare={handleShare}
                      searchTerm={filters.searchTerm}
                      allMembers={members}
                      connectionStatus={getConnectionStatus(member)}
                    />
                  );
                })}
                
                {/* Load More Button for List View */}
                {hasMore && (
                  <div className="flex justify-center py-6">
                    <button
                      onClick={loadMoreMembers}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? 'Chargement...' : 'Charger plus de membres'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {filters.viewMode === 'map' && (
              <MemberMapView
                members={allMembers}
                onMemberSelect={handleViewProfile}
                onClusterSelect={handleClusterSelect}
              />
            )}

            {filters.viewMode === 'analytics' && (
              <DirectoryAnalytics members={allMembers} />
            )}

            {/* Empty state */}
            {members.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="text-muted-foreground text-lg">
                  Aucun membre ne correspond à vos critères de recherche.
                </div>
                <button 
                  onClick={resetFilters}
                  className="mt-4 text-primary hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8">
            <SubscriptionRestriction
              requiredTier="basic"
              feature="Annuaire des membres"
              description="L'accès à l'annuaire des membres nécessite un abonnement actif."
            />
          </div>
        )}
      </main>

      {/* Connection Request Dialog */}
      <ConnectionRequestDialog 
        isOpen={isConnectionDialogOpen}
        setIsOpen={setIsConnectionDialogOpen}
        selectedMember={selectedMember}
        onSendRequest={sendRequest}
      />

      {/* Member Profile Modal */}
      <MemberProfileModal
        member={selectedMember}
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onMessage={handleMessage}
        onConnect={handleRequestConnection}
        onFavorite={handleFavorite}
        onShare={handleShare}
        currentUserId={user?.id}
      />

      <Footer />
    </div>
  );
};


export default Members;
